import { LightbulbIcon } from '@phosphor-icons/react';
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { WidgetOverlay } from '../../../components/WidgetOverlay';
import { useDragScroll } from '../useDragScroll';
import {
  LIGHT_FLOOR_ORDER,
  LIGHT_ROOMS,
  bulkOffRooms,
  litEntities,
  roomLightState,
  roomsOnFloor,
  shortRoomLabel,
} from './lights';
// The whole-home isometric line drawing (all three floors pre-stacked with
// connector lines), sanitized by a one-off script: C2PA metadata + labels
// stripped, colors remapped to the CSS vars set on the map container below
// (--fp-wall = plan lines, --fp-line = connectors, --fp-fill = occlusion
// fills matching the card background so upper floors mask lower ones).
import { ROOM_LABELS } from './roomLabels';
import {
  BADGE_CLASS,
  CAMERA_DEFS,
  CAMERA_ENTITY_SET,
  CONTACT_DEFS,
  CONTACT_LINGER_MS,
  EVENT_STYLE,
  FLOOR_GROUP_ID,
  FLOOR_LABELS,
  FLOOR_TY,
  ISO_A,
  ISO_TX,
  ISO_TY,
  MOTION_DEFS,
  PING_ALIVE,
  PING_PERIOD_MS,
  STATE_BADGE,
  badgeStyle,
  contactState,
  fetchSecurityEvents,
  fmtClock,
  headerMotionLabel,
  isUnavailable,
  previewLit,
  roomKey,
  sensorFloor,
  useSecurityState,
  type ContactDef,
  type EntityState,
  type FloorId,
  type MotionDef,
  type SecEvent,
  type SecurityWidgetProps,
} from './houseState';
import wholeHomePlan from '../../../assets/floorplan/whole-home.svg?raw';

// Kitchen kiosk security widget — the whole-home map: Dakota's isometric
// line drawing of all three floors (neutral gray linework), with live sensor
// state drawn on top.
//
// Semantics: each door/window is its wall opening lit as a glowing segment —
// green when secure, red + pulse when open. Motion sensors are dots at the
// room center — gray at rest, amber halo + pulse during motion. Unavailable
// sensors dim/hollow out.
//
// Sensor positions are in the CubiCasa plan coordinate space (the drawing
// reuses it under one shared isometric matrix) and are projected through
// that matrix plus the floor's vertical offset. Nudge cx/cy to fine-tune;
// HMR makes that a live edit.
//
// The alarm panel entity (arm state) is alarm_control_panel.bothell_alarm —
// a previous iteration showed it as a header chip; removed by request.

export function SecurityWidget({ className }: SecurityWidgetProps = {}) {
  const {
    states: viewStates,
    error,
    nowTick,
    closedAt,
    openContacts,
    activeMotion,
    litRoomCount,
    applyLights,
  } = useSecurityState();

  // Fullscreen expansion (same overlay pattern as the music browser). The
  // small map's screen rect is captured at tap time so the modal's map can
  // FLIP-animate: it mounts fullscreen, then starts transformed down to the
  // captured rect and transitions to identity — the small map grows into the
  // large one.
  const [expanded, setExpanded] = useState(false);
  const smallMapRef = useRef<HTMLDivElement | null>(null);
  const sourceRectRef = useRef<DOMRect | null>(null);
  function openExpanded(): void {
    sourceRectRef.current = smallMapRef.current?.getBoundingClientRect() ?? null;
    setExpanded(true);
  }

  // Header row — shared between the widget and the expanded overlay.
  const header = (
    <div className="flex items-center gap-3 min-w-0 w-full">
      {activeMotion.length > 0 && (
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          {activeMotion.map((m) => (
            <span
              key={m.entity}
              className={BADGE_CLASS}
              style={
                CAMERA_ENTITY_SET.has(m.entity)
                  ? badgeStyle('--berg-blue-light', '--berg-blue-soft')
                  : badgeStyle('--berg-purple-light', '--berg-purple-soft')
              }
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: CAMERA_ENTITY_SET.has(m.entity)
                    ? 'var(--berg-blue-light)'
                    : 'var(--berg-purple-light)',
                }}
              />
              {headerMotionLabel(m.label)}
            </span>
          ))}
        </div>
      )}
      <div className="ml-auto flex items-center gap-2">
        {/* How many rooms have a light on — the map shows WHICH ones. */}
        {litRoomCount > 0 && (
          <span className={BADGE_CLASS} style={badgeStyle('--berg-yellow', '--berg-yellow-soft')}>
            <LightbulbIcon size={13} weight="fill" />
            {litRoomCount}
          </span>
        )}
        {error ? (
          <span className="text-xs text-fg-tertiary">{error}</span>
        ) : (
          <span
            className={BADGE_CLASS}
            style={badgeStyle(
              openContacts.length === 0 ? '--berg-green' : '--berg-red',
              openContacts.length === 0 ? '--berg-green-soft' : '--berg-red-soft'
            )}
          >
            <span className="relative flex w-1.5 h-1.5">
              <span
                className="absolute inline-flex h-full w-full rounded-full animate-ping"
                style={{ backgroundColor: 'currentColor', opacity: 0.6 }}
              />
              <span
                className="relative inline-flex w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: 'currentColor' }}
              />
            </span>
            {openContacts.length === 0 ? 'All secure' : `${openContacts.length} open`}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Deliberately not a card — the house map floats directly on the kiosk
          canvas (no chrome), with the status badge hovering above it. The
          whole widget is a tap target that expands the map fullscreen. */}
      <button
        type="button"
        onClick={openExpanded}
        aria-label="Expand security map"
        className={`flex flex-col gap-2 w-full h-full min-h-0 text-left cursor-pointer ${className ?? ''}`}
      >
        {header}
        <div ref={smallMapRef} className="relative flex-1 min-h-0 w-full">
          <HouseMap states={viewStates} nowTick={nowTick} closedAt={closedAt} />
        </div>
      </button>
      {expanded && (
        <SecurityMapOverlay
          onClose={() => setExpanded(false)}
          sourceRect={sourceRectRef.current}
          header={header}
          states={viewStates}
          nowTick={nowTick}
          closedAt={closedAt}
          onLights={applyLights}
        />
      )}
    </>
  );
}

// Fullscreen overlay for the expanded map, in the shared WidgetOverlay shell
// (which owns the backdrop, close button, and the grow-from-the-widget
// open/close animation). Interactive extras: per-floor visibility toggles (top
// left), tap-a-sensor detail card, and a 24h event timeline along the bottom
// (tap an event to flash its sensor on the map).
export function SecurityMapOverlay({
  onClose,
  sourceRect,
  header,
  states,
  nowTick,
  closedAt,
  onLights,
}: {
  onClose: () => void;
  sourceRect: DOMRect | null;
  header: React.ReactNode;
  states: Record<string, EntityState>;
  nowTick: number;
  closedAt: Map<string, number>;
  onLights: (entityIds: string[], on: boolean) => void;
}) {
  // 24h event history — fetched on open, refreshed while the overlay is up.
  const [events, setEvents] = useState<SecEvent[] | null>(null);
  useEffect(() => {
    let live = true;
    const load = () =>
      fetchSecurityEvents()
        .then((ev) => live && setEvents(ev))
        .catch(() => live && setEvents((prev) => prev ?? []));
    void load();
    const t = setInterval(load, 30_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  // Tap-a-sensor detail card + tap-an-event location flash.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<{ key: string; until: number } | null>(null);
  function flash(key: string): void {
    setHighlight({ key: key, until: Date.now() + 3_000 });
  }
  const highlightKey = highlight && highlight.until > nowTick ? highlight.key : null;

  // Both bottom rails pan by finger-drag (the touchscreen won't scroll them
  // natively — see useDragScroll).
  const eventRail = useDragScroll();
  const lightsRail = useDragScroll();

  // Floor toggles are a SOLO control: tap a floor to show only it — the
  // floor un-tilts from the isometric stack into a flat top-down plan while
  // the other floors fade out. Tap again to restack.
  const [soloFloor, setSoloFloor] = useState<FloorId | null>(null);

  const selectedContact = selectedKey
    ? CONTACT_DEFS.find((d) => d.entities[0] === selectedKey)
    : undefined;
  const selectedMotion = selectedKey
    ? (MOTION_DEFS.find((d) => d.entity === selectedKey) ??
      CAMERA_DEFS.find((d) => d.entity === selectedKey))
    : undefined;
  const selectedLabel = selectedContact?.label ?? selectedMotion?.label;
  const selectedStateWord = selectedContact
    ? contactState(selectedContact, states).open
      ? 'Open'
      : 'Closed'
    : selectedMotion
      ? states[selectedMotion.entity]?.state === 'on'
        ? 'Motion'
        : 'Clear now'
      : '';
  const selectedEvents = selectedKey
    ? (events ?? []).filter((e) => e.key === selectedKey).slice(0, 5)
    : [];

  // While a floor is soloed the timeline shows only that floor's sensors.
  const visibleEvents = soloFloor
    ? (events ?? []).filter((e) => sensorFloor(e.key) === soloFloor)
    : (events ?? []);
  const eventEdges = useScrollEdges(eventRail.ref, `${events === null}:${visibleEvents.length}`);

  return (
    <WidgetOverlay
      onClose={onClose}
      sourceRect={sourceRect}
      label="House map"
      autoCloseMs={OVERLAY_AUTOCLOSE_MS}
      toolbar={
        // Floor visibility toggles — centered in the top bar. A touch tighter
        // on a phone so they share the row with the title and close button.
        <div className="flex items-center gap-1.5 sm:gap-2">
          {(['upper', 'main', 'basement'] as FloorId[]).map((f) => {
            const on = soloFloor === null || soloFloor === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setSoloFloor((cur) => (cur === f ? null : f))}
                aria-pressed={on}
                className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  on
                    ? 'bg-elevated border-border-strong text-fg'
                    : 'bg-transparent border-border text-fg-tertiary'
                }`}
              >
                {FLOOR_LABELS[f]}
              </button>
            );
          })}
        </div>
      }
      header={header}
      // Taps on floors/sensors stop propagation, so a click landing on the
      // body is empty space: in the flat view it restacks the perspective; in
      // the perspective view it closes the overlay.
      onBodyClick={(close) => {
        if (soloFloor) setSoloFloor(null);
        else close();
      }}
      footer={
        <>
          {/* Lights: everything off, or a single room on/off. */}
          <LightsRail states={states} onLights={onLights} rail={lightsRail} floor={soloFloor} />

          {/* 24h event timeline — most recent first; tap to flash the location.
              Soloing a floor narrows this to that floor's sensors, so the rails
              and the map are describing the same place. Edge to edge with
              gutter-only fades, like the lights rail above. */}
          <div className="relative shrink-0 -mx-6">
            <div
              ref={eventRail.ref}
              {...eventRail.handlers}
              className="flex items-center gap-2 overflow-x-auto scrollbar-hidden touch-pan-x px-6"
              style={edgeFadeMask(eventEdges)}
            >
              {events === null ? (
                <span className="text-xs text-fg-tertiary">Loading events…</span>
              ) : visibleEvents.length === 0 ? (
                <span className="text-xs text-fg-tertiary">
                  {soloFloor
                    ? `No ${FLOOR_LABELS[soloFloor].toLowerCase()}-floor events in the last 24h`
                    : 'No events in the last 24h'}
                </span>
              ) : (
                visibleEvents.map((e) => (
                  <button
                    key={`${e.key}-${e.at}-${e.action}`}
                    type="button"
                    onClick={() => flash(e.key)}
                    // Two short lines — the sensor, then what it did and when
                    // — so each chip is about half the width of the one-line
                    // version and more of the timeline fits on screen.
                    className="shrink-0 flex items-start gap-2 pl-2.5 pr-3 py-1.5 rounded-xl border border-border bg-elevated text-left"
                  >
                    <span
                      className="shrink-0 mt-[5px] w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: `var(${EVENT_STYLE[e.action].colorVar})` }}
                    />
                    <span className="flex flex-col leading-tight whitespace-nowrap">
                      <span className="text-xs font-medium">{e.label}</span>
                      <span className="text-[11px]">
                        <span className="text-fg-secondary">{EVENT_STYLE[e.action].verb}</span>{' '}
                        <span className="text-fg-tertiary">{fmtClock(e.at)}</span>
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      }
    >
      <HouseMap
        states={states}
        nowTick={nowTick}
        closedAt={closedAt}
        focusFloor={soloFloor}
        highlightKey={highlightKey}
        onSelect={(key) => setSelectedKey((cur) => (cur === key ? null : key))}
        onFloorTap={(f) => setSoloFloor((cur) => (cur === f ? null : f))}
      />
      {/* Detail card for the tapped sensor — tap it to dismiss. */}
      {selectedKey && selectedLabel && (
        <button
          type="button"
          onClick={(ev) => {
            ev.stopPropagation();
            setSelectedKey(null);
          }}
          // max-w-full matters on a narrow phone: w-80 is 320px and a 360px
          // Android viewport leaves only 312px inside the overlay padding.
          className="absolute left-0 top-0 card !p-4 w-80 max-w-full text-left flex flex-col gap-2"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold">{selectedLabel}</span>
            {(() => {
              const badge = STATE_BADGE[selectedStateWord];
              if (!badge) return null;
              return (
                <span className={BADGE_CLASS} style={badgeStyle(badge.fg, badge.bg)}>
                  {selectedStateWord}
                </span>
              );
            })()}
          </div>
          {events === null ? (
            <span className="text-xs text-fg-tertiary">Loading history…</span>
          ) : selectedEvents.length === 0 ? (
            <span className="text-xs text-fg-tertiary">No events in the last 24h</span>
          ) : (
            <div className="flex flex-col gap-1">
              {selectedEvents.map((e) => (
                <div key={`${e.at}-${e.action}`} className="flex items-center gap-2 text-xs">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: `var(${EVENT_STYLE[e.action].colorVar})` }}
                  />
                  <span className="text-fg-secondary">{EVENT_STYLE[e.action].verb}</span>
                  <span className="ml-auto text-fg-tertiary">{fmtClock(e.at)}</span>
                </div>
              ))}
            </div>
          )}
        </button>
      )}
    </WidgetOverlay>
  );
}

// The overlay's light controls, in the three levels the kiosk needs: the whole
// house off, one floor off, one room on or off. Bulk switches are off-only by
// design — "turn the house on" isn't a thing anyone wants at a wall panel, and
// it would flood every room at once.
function LightsRail({
  states,
  onLights,
  rail,
  floor,
}: {
  states: Record<string, EntityState>;
  onLights: (entityIds: string[], on: boolean) => void;
  rail: ReturnType<typeof useDragScroll>;
  // When a floor is soloed the rail narrows to that floor's rooms, matching
  // the map. All off stays whole-house — it's the one control you want to be
  // able to reach without first backing out to the stacked view.
  floor: FloorId | null;
}) {
  // The Ring floodlights sit out of the bulk switches (see lights.ts) but stay
  // individually controllable as the Outdoor room.
  const unassigned = LIGHT_ROOMS.filter((r) => r.floor === null);
  const rooms = floor
    ? roomsOnFloor(floor)
    : [...LIGHT_FLOOR_ORDER.flatMap((f) => roomsOnFloor(f)), ...unassigned];
  // Soloing a floor narrows the bulk switch to that floor too — it should
  // only ever turn off what you can currently see.
  // The label stays "All Off" either way — in a soloed view "all" reads as
  // all of what's on screen, and renaming it per floor just adds noise.
  const bulkRooms = floor ? rooms.filter((r) => !r.excludeFromBulkOff) : bulkOffRooms();
  const bulkLit = litEntities(bulkRooms, states);
  const edges = useScrollEdges(rail.ref, rooms.length);

  // Tiles, not pills: the event rail below is all pill chips, and a control
  // that changes the house shouldn't look like a log entry you can tap for
  // detail. Bigger, squarer, and easier to hit from arm's length. On a phone
  // (the home screen's overlay) they step down a size so the rail shows
  // more than a room or two at once; the kiosk is well past `sm`.
  const tile =
    'shrink-0 w-[72px] h-[72px] sm:w-24 sm:h-24 flex flex-col items-start justify-between p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border text-left transition-colors active:scale-[0.97]';
  const tileIcon = 'w-[18px] h-[18px] sm:w-5 sm:h-5';
  const tileLabel = 'text-[11px] sm:text-sm font-semibold leading-tight';

  return (
    // The rail bleeds through the overlay's side padding to both screen
    // edges, and fades out only inside that gutter, so every tile short of
    // the edge reads at full strength. A side fades only while there's more
    // to scroll toward it: the left once you've scrolled, the right until the
    // end — a half-faded tile reads as "keep scrolling".
    <div className="relative shrink-0 -mx-6">
      <div
        ref={rail.ref}
        {...rail.handlers}
        className="flex items-stretch gap-2 overflow-x-auto scrollbar-hidden touch-pan-x px-6"
        style={edgeFadeMask(edges)}
      >
        <button
          type="button"
          disabled={bulkLit.length === 0}
          onClick={(ev) => {
            ev.stopPropagation();
            onLights(bulkLit, false);
          }}
          className={`${tile} ${
            bulkLit.length === 0
              ? 'bg-transparent border-border text-fg-tertiary'
              : 'bg-elevated border-border-strong text-fg'
          }`}
        >
          <LightbulbIcon
            weight={bulkLit.length === 0 ? 'regular' : 'fill'}
            className={tileIcon}
          />
          <span className={tileLabel}>All Off</span>
        </button>

        <span className="shrink-0 w-px my-2 bg-border" />

        {/* One tile per room, floors first then the rooms still unmapped. */}
        {rooms.map((room) => {
          const st = roomLightState(room, states);
          return (
            <button
              key={room.key}
              type="button"
              disabled={st.offline}
              onClick={(ev) => {
                ev.stopPropagation();
                onLights(room.entities, !st.on);
              }}
              className={`${tile} ${st.offline ? 'opacity-40' : ''} ${
                st.on ? '' : 'bg-elevated border-border text-fg-secondary'
              }`}
              style={
                st.on
                  ? {
                      color: 'var(--berg-yellow)',
                      backgroundColor: 'var(--berg-yellow-soft)',
                      borderColor: 'color-mix(in srgb, var(--berg-yellow) 45%, transparent)',
                    }
                  : undefined
              }
            >
              <LightbulbIcon
                weight={st.on ? 'fill' : 'regular'}
                className={tileIcon}
                style={
                  st.on ? { filter: 'drop-shadow(0 0 6px var(--berg-yellow-glow))' } : undefined
                }
              />
              <span className={tileLabel}>
                {shortRoomLabel(room.label)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// The rail's edge fades span the overlay's px-6 gutter.
const EDGE_FADE_PX = 24;

function edgeFadeMask(edges: { start: boolean; end: boolean }): CSSProperties {
  const left = edges.start ? 0 : EDGE_FADE_PX;
  const right = edges.end ? 0 : EDGE_FADE_PX;
  const mask = `linear-gradient(to right, transparent, black ${left}px, black calc(100% - ${right}px), transparent)`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

// Whether a horizontal scroller is at its start / end, kept current through
// scrolling, resizes, and content changes (`contentKey`).
function useScrollEdges(
  ref: { current: HTMLDivElement | null },
  contentKey: unknown
): { start: boolean; end: boolean } {
  const [edges, setEdges] = useState({ start: true, end: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const start = el.scrollLeft <= 1;
      const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setEdges((cur) => (cur.start === start && cur.end === end ? cur : { start, end }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [ref, contentKey]);
  return edges;
}

// The whole-home map body — the isometric line drawing with live sensor
// overlays, sized by its container so the widget and the fullscreen overlay
// render the same thing at different scales. The drawing stays neutral gray
// (color is reserved for sensors); --fp-fill matches the card background so
// upper floors occlude the ones below. Each instance runs its own rAF ping
// clock, but the clocks derive from the same wall-time modulo, so instances
// stay in phase with each other by construction.
const VIEWBOX = '-997 -1572 2250 3060';
const VB = { x: -997, y: -1572, w: 2250, h: 3060 };
const FLOOR_ORDER: FloorId[] = ['upper', 'main', 'basement'];

// CSS transform strings for the two presentations of a floor's plan
// geometry. Both the drawing's plan groups and the sensor overlay's groups
// carry these, so a CSS transition on `transform` morphs the tilted
// isometric floor into a flat top-down plan (and back) with the sensors
// riding along in perfect registration.
function isoOverlayCss(f: FloorId): string {
  return `matrix(${ISO_A}, 0.5, ${-ISO_A}, 0.5, ${ISO_TX}, ${ISO_TY + FLOOR_TY[f]})`;
}
// The drawing's plan groups sit inside a parent translate(0, FLOOR_TY), so
// their CSS omits the floor offset (it stays on the parent).
const ISO_DRAWING_CSS = `matrix(${ISO_A}, 0.5, ${-ISO_A}, 0.5, ${ISO_TX}, ${ISO_TY})`;

interface PlanBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Flat presentation: scale the floor's plan bbox up to fill the viewBox,
// centered. Returns [scale, translateX, translateY] in viewBox units.
function flatParams(b: PlanBox): [number, number, number] {
  const s = 0.92 * Math.min(VB.w / b.w, VB.h / b.h);
  const tx = VB.x + (VB.w - s * b.w) / 2 - s * b.x;
  const ty = VB.y + (VB.h - s * b.h) / 2 - s * b.y;
  return [s, tx, ty];
}

// The expanded overlay closes itself after this much inactivity — the kiosk
// should drift back to the dashboard, not sit on a forgotten map.
const OVERLAY_AUTOCLOSE_MS = 30_000;

// A room's footprint in plan coordinates: one entry per polygon the drawing
// splits it into.
interface RoomGeom {
  floor: FloorId;
  shapes: Array<Array<[number, number]>>;
}

// One path covering every polygon the room spans.
function roomPathD(g: RoomGeom): string {
  return g.shapes.map((s) => `M ${s.map(([x, y]) => `${x},${y}`).join(' L ')} Z`).join(' ');
}

// Half of the lit room's border width — the outline is stroked at double this
// and masked to the outside of the room, which is what erases the seam where
// two of the drawing's polygons meet inside one room.
const ROOM_BORDER_W = 4;

// Ray-cast point-in-polygon, used to match a room label to the drawing's own
// room polygon.
function pointInPolygon(x: number, y: number, pts: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// Trim a polygon to one side of a line (Sutherland-Hodgman against a single
// half-plane): everything right of travelling a->b is dropped and the polygon
// is closed along the line, so a cut room keeps a clean straight edge and
// stays a single simple polygon.
function clipToLine(
  poly: Array<[number, number]>,
  ax: number,
  ay: number,
  bx: number,
  by: number
): Array<[number, number]> {
  const side = (px: number, py: number) => (bx - ax) * (py - ay) - (by - ay) * (px - ax);
  const out: Array<[number, number]> = [];
  for (let i = 0; i < poly.length; i++) {
    const cur = poly[i];
    const nxt = poly[(i + 1) % poly.length];
    const sc = side(cur[0], cur[1]);
    const sn = side(nxt[0], nxt[1]);
    if (sc >= 0) out.push(cur);
    if (sc >= 0 !== sn >= 0) {
      const t = sc / (sc - sn);
      out.push([cur[0] + (nxt[0] - cur[0]) * t, cur[1] + (nxt[1] - cur[1]) * t]);
    }
  }
  return out;
}

const MORPH_TRANSITION = 'transform 450ms cubic-bezier(0.2, 0, 0.2, 1)';
const FADE_TRANSITION = 'opacity 300ms ease';

function HouseMap({
  states,
  nowTick,
  closedAt,
  focusFloor,
  highlightKey,
  onSelect,
  onFloorTap,
}: {
  states: Record<string, EntityState>;
  nowTick: number;
  closedAt: Map<string, number>;
  focusFloor?: FloorId | null;
  highlightKey?: string | null;
  onSelect?: (key: string) => void;
  // Tap a floor's footprint to solo/unsolo it (expanded overlay only).
  onFloorTap?: (f: FloorId) => void;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  // Unique prefix for SVG ids — the widget and the overlay both mount a map.
  const uid = useId().replace(/:/g, '');
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const el = mapRef.current;
      if (el) {
        const t = (Date.now() % PING_PERIOD_MS) / PING_PERIOD_MS;
        const q = Math.min(t / PING_ALIVE, 1);
        const eased = 1 - Math.pow(1 - q, 3); // ~cubic ease-out
        const alive = t < PING_ALIVE ? 1 : 0;
        el.style.setProperty('--ping-grow', eased.toFixed(4));
        el.style.setProperty('--ping-fade', (alive * (1 - eased)).toFixed(4));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Each floor's plan bbox in CubiCasa plan space, measured once from the
  // injected drawing (plan.getBBox() excludes the group's own iso matrix, so
  // these ARE plan coordinates). Drives the flat-presentation transform.
  const [planBoxes, setPlanBoxes] = useState<Partial<Record<FloorId, PlanBox>>>({});
  useEffect(() => {
    const boxes: Partial<Record<FloorId, PlanBox>> = {};
    for (const f of FLOOR_ORDER) {
      const plan = mapRef.current?.querySelector(
        `#${FLOOR_GROUP_ID[f]} > .plan`
      ) as SVGGraphicsElement | null;
      if (!plan) continue;
      try {
        const b = plan.getBBox();
        boxes[f] = { x: b.x, y: b.y, w: b.width, h: b.height };
      } catch {
        // leave unmeasured; that floor just won't morph flat
      }
    }
    setPlanBoxes(boxes);
  }, []);

  // Lit rooms are painted onto the DRAWING's own room polygons rather than
  // shapes of our own: the CubiCasa plan already carries one filled polygon
  // per room (they're the occlusion fills that let an upper floor mask the
  // one below), so a room's real outline and floor are already there to
  // borrow. Each room is found once, by hit-testing its label anchor against
  // every polygon on that floor and keeping the smallest one that contains
  // it — the smallest, because the floor slab contains every anchor too.
  // The same rooms in plan coordinates, for drawing the merged outline.
  const [roomGeom, setRoomGeom] = useState<Map<string, RoomGeom>>(new Map());
  useEffect(() => {
    const geom = new Map<string, RoomGeom>();
    // Every labelled room is resolved, not just the ones with lights — so
    // ?lit= can preview a boundary before any switch exists for that room.
    for (const r of ROOM_LABELS) {
      const plan = mapRef.current?.querySelector(
        `#${FLOOR_GROUP_ID[r.floor]} > .plan`
      ) as SVGGraphicsElement | null;
      if (!plan) continue;
      const planCtm = plan.getScreenCTM();
      if (!planCtm) continue;
      const inv = planCtm.inverse();
      const polys = Array.from(plan.querySelectorAll('polygon'));

      // A room can span several of the drawing's polygons — a master bath and
      // its separate toilet closet are two shapes but one room. Each anchor
      // resolves independently and they're painted together.
      const els: SVGPolygonElement[] = [];
      const shapes: Array<Array<[number, number]>> = [];
      for (const [ax, ay] of [[r.x, r.y], ...(r.alsoAt ?? [])]) {
        let best: { el: SVGPolygonElement; area: number; pts: Array<[number, number]> } | null =
          null;
        for (const poly of polys) {
          const ctm = poly.getScreenCTM();
          if (!ctm) continue;
          const toPlan = inv.multiply(ctm);
          const pts: Array<[number, number]> = [];
          for (let i = 0; i < poly.points.numberOfItems; i++) {
            const p = poly.points.getItem(i).matrixTransform(toPlan);
            pts.push([p.x, p.y]);
          }
          if (pts.length < 3 || !pointInPolygon(ax, ay, pts)) continue;
          let a = 0;
          for (let i = 0; i < pts.length; i++) {
            const [x1, y1] = pts[i];
            const [x2, y2] = pts[(i + 1) % pts.length];
            a += x1 * y2 - x2 * y1;
          }
          a = Math.abs(a) / 2;
          if (!best || a < best.area) best = { el: poly, area: a, pts };
        }
        if (best && !els.includes(best.el)) {
          els.push(best.el);
          // Apply the room's cuts; a cut that removes a shape entirely
          // just drops it.
          let pts = best.pts;
          for (const [cax, cay, cbx, cby] of r.cuts ?? []) {
            pts = clipToLine(pts, cax, cay, cbx, cby);
          }
          if (pts.length >= 3) shapes.push(pts);
        }
      }
      if (shapes.length) {
        geom.set(roomKey(r), { floor: r.floor, shapes });
      }
    }
    setRoomGeom(geom);
    // Keyed on the definitions, not [] — otherwise editing a room's anchors,
    // cuts or subtractions hot-reloads the module while the map keeps the
    // shapes it resolved at page load, and the change looks like it did
    // nothing until a full reload. The lint rule objects because a module
    // constant can't re-render the component, which is true; what this relies
    // on is HMR REPLACING the module, giving a new array identity. In a
    // production build the identity never changes and it runs exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ROOM_LABELS]);

  // Lit rooms are painted INSIDE each floor's plan group — portalled into a
  // host <g> appended to it — not in the sensor overlay. The overlay sits on
  // top of the whole drawing, so a lit basement room painted over the main
  // level in the perspective stack. Inside the plan the wash shares its
  // floor's transform, fade and stacking, so the floors above cover it the
  // same way they cover the floor's own rooms. Plan coordinates are the
  // plan group's own, so roomGeom drops in unchanged.
  const [litHosts, setLitHosts] = useState<Partial<Record<FloorId, SVGGElement>>>({});
  useEffect(() => {
    const hosts: Partial<Record<FloorId, SVGGElement>> = {};
    for (const f of FLOOR_ORDER) {
      const plan = mapRef.current?.querySelector(`#${FLOOR_GROUP_ID[f]} > .plan`);
      if (!plan) continue;
      const host = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      host.setAttribute('pointer-events', 'none');
      plan.appendChild(host);
      hosts[f] = host;
    }
    setLitHosts(hosts);
    return () => {
      for (const host of Object.values(hosts)) host?.remove();
    };
  }, []);

  // Which rooms read as lit right now — real state, or a ?lit= / PREVIEW_ROOM
  // dry run that never touches a switch.
  const litSlugs = new Set<string>();
  for (const r of ROOM_LABELS) {
    const room = r.lightKey ? LIGHT_ROOMS.find((lr) => lr.key === r.lightKey) : undefined;
    if (previewLit(r.label, r.lightKey) || (room && roomLightState(room, states).on)) {
      litSlugs.add(roomKey(r));
    }
  }

  function drawingCssFor(f: FloorId): string {
    const b = planBoxes[f];
    if (focusFloor === f && b) {
      const [sc, tx, ty] = flatParams(b);
      // Parent group keeps translate(0, FLOOR_TY), so cancel it here.
      return `matrix(${sc}, 0, 0, ${sc}, ${tx}, ${ty - FLOOR_TY[f]})`;
    }
    return ISO_DRAWING_CSS;
  }
  function overlayCssFor(f: FloorId): string {
    const b = planBoxes[f];
    if (focusFloor === f && b) {
      const [sc, tx, ty] = flatParams(b);
      return `matrix(${sc}, 0, 0, ${sc}, ${tx}, ${ty})`;
    }
    return isoOverlayCss(f);
  }

  // What a tap on a sensor means depends on the view. In the flat (soloed)
  // view it opens that sensor's detail card. In the perspective stack it solos
  // the floor, exactly as tapping the floor around it does — a sensor is part
  // of its floor, not a hole in it. Returning undefined would leave the marker
  // with no handler, and the tap would fall through to the overlay body, which
  // reads a stray click as "close". That is the bug this replaced: two of the
  // cameras sit outside their floor's bbox (mounted beyond the wall), so not
  // even the floor rect was under them to catch it.
  function sensorTap(f: FloorId, key: string): (() => void) | undefined {
    if (onSelect && focusFloor === f) return () => onSelect(key);
    if (onFloorTap && !focusFloor) return () => onFloorTap(f);
    return undefined;
  }

  const drawingStyles =
    FLOOR_ORDER.map((f) => {
      const dimmed = focusFloor && focusFloor !== f;
      return (
        `#${FLOOR_GROUP_ID[f]} > .plan{transform:${drawingCssFor(f)};transform-box:view-box;transform-origin:0 0;transition:${MORPH_TRANSITION};}` +
        `#${FLOOR_GROUP_ID[f]}{opacity:${dimmed ? 0 : 1};transition:${FADE_TRANSITION};}`
      );
    }).join('\n') + `\n.connectors{opacity:${focusFloor ? 0 : 1};transition:${FADE_TRANSITION};}`;

  return (
    <div
      ref={mapRef}
      className="absolute inset-0"
      style={{
        ['--fp-wall' as string]: 'var(--berg-text-secondary)',
        ['--fp-line' as string]: 'var(--berg-text-tertiary)',
        ['--fp-fill' as string]: 'var(--berg-bg-elevated)',
      }}
    >
      {/* Presentation styles for the injected drawing: per-floor plan
          transforms (iso <-> flat morph) + fades for non-soloed floors. */}
      <style>{drawingStyles}</style>
      <div
        className="absolute inset-0 [&_svg]:w-full [&_svg]:h-full"
        // Sanitized static asset from this repo, not remote/user content.
        dangerouslySetInnerHTML={{ __html: wholeHomePlan }}
      />
      {/* Sensor overlay — sensors are drawn in raw plan coordinates inside
          per-floor groups that carry the SAME transform as the drawing's
          plan groups, so they morph in lockstep with the floor. */}
      <svg
        className="absolute inset-0 w-full h-full overflow-visible"
        viewBox={VIEWBOX}
        aria-hidden="true"
      >
        {FLOOR_ORDER.map((f) => {
          const dimmed = focusFloor && focusFloor !== f;
          return (
            <g
              key={f}
              style={{
                transform: overlayCssFor(f),
                transformBox: 'view-box',
                transformOrigin: '0 0',
                transition: `${MORPH_TRANSITION}, ${FADE_TRANSITION}`,
                opacity: dimmed ? 0 : 1,
                pointerEvents: dimmed ? 'none' : undefined,
              }}
            >
              {/* Whole-floor tap target — covers the floor's plan footprint
                  (transformed with the group, so it tracks iso and flat).
                  First in paint order so sensor hit targets win over it.
                  Not rendered for a faded-out floor: the group's
                  pointer-events:none does NOT stop a child that sets
                  pointer-events:all, so in the flat view the hidden floors
                  would keep catching taps meant for empty space. */}
              {onFloorTap && planBoxes[f] && !dimmed && (
                <rect
                  x={planBoxes[f]!.x}
                  y={planBoxes[f]!.y}
                  width={planBoxes[f]!.w}
                  height={planBoxes[f]!.h}
                  fill="#000"
                  fillOpacity={0}
                  style={{ pointerEvents: 'all', cursor: 'pointer' }}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onFloorTap(f);
                  }}
                />
              )}
              {CONTACT_DEFS.filter((d) => d.floor === f).map((d) => {
                const closed = closedAt.get(d.entities[0]);
                const lingerAlpha = closed
                  ? Math.max(0, 1 - (nowTick - closed) / CONTACT_LINGER_MS)
                  : 0;
                const cs = contactState(d, states);
                return (
                  <ContactSegment
                    key={d.entities[0]}
                    def={d}
                    open={cs.open}
                    offline={cs.offline}
                    lingerAlpha={lingerAlpha}
                    highlighted={highlightKey === d.entities[0]}
                    onSelect={sensorTap(f, d.entities[0])}
                  />
                );
              })}
              {MOTION_DEFS.filter((d) => d.floor === f).map((d) => (
                <MotionDot
                  key={d.entity}
                  def={d}
                  state={states[d.entity]?.state}
                  highlighted={highlightKey === d.entity}
                  onSelect={sensorTap(f, d.entity)}
                />
              ))}
              {/* Ring cameras — same cone, blue instead of purple. Drawn after
                  the motion sensors so a camera sitting near one stays
                  readable. */}
              {CAMERA_DEFS.filter((d) => d.floor === f).map((d) => (
                <MotionDot
                  key={d.entity}
                  def={d}
                  state={states[d.entity]?.state}
                  highlighted={highlightKey === d.entity}
                  onSelect={sensorTap(f, d.entity)}
                  tint="var(--berg-blue-light)"
                  pointed
                />
              ))}
            </g>
          );
        })}
      </svg>
      {/* Lit-room washes, one portal per floor into that floor's plan group
          (see litHosts). Every polygon of the room is stroked at double
          width, then masked to the OUTSIDE of the room, so only the outer
          half of the outer boundary survives — any edge interior to the room
          (where the drawing splits a bathroom from its toilet closet, or a
          hall from its landing) is masked away and the room reads as one
          shape. */}
      {FLOOR_ORDER.map((f) => {
        const host = litHosts[f];
        const b = planBoxes[f];
        if (!host || !b) return null;
        const pad = 400;
        return createPortal(
          [...roomGeom].map(([slug, g]) => {
            if (g.floor !== f || !litSlugs.has(slug)) return null;
            const maskId = `${uid}-room-${slug}`;
            const d = roomPathD(g);
            return (
              <g
                key={slug}
                className="floorplan-glow"
                style={{
                  ['--fp-glow' as string]: 'var(--berg-yellow-glow)',
                  ['--fp-glow-size' as string]: '22px',
                }}
              >
                <mask
                  id={maskId}
                  maskUnits="userSpaceOnUse"
                  x={b.x - pad}
                  y={b.y - pad}
                  width={b.w + pad * 2}
                  height={b.h + pad * 2}
                >
                  <rect
                    x={b.x - pad}
                    y={b.y - pad}
                    width={b.w + pad * 2}
                    height={b.h + pad * 2}
                    fill="white"
                  />
                  <path d={d} fill="black" />
                </mask>
                {/* Floor wash. Translucent, so the room's fixtures and
                    furniture still read through it. */}
                <path d={d} fill="var(--berg-yellow-soft)" />
                <g mask={`url(#${maskId})`}>
                  <path
                    d={d}
                    fill="none"
                    stroke="var(--berg-yellow)"
                    strokeWidth={ROOM_BORDER_W * 2}
                    strokeOpacity={0.85}
                  />
                </g>
              </g>
            );
          }),
          host,
          f
        );
      })}
    </div>
  );
}

// A contact sensor's opening, drawn along the wall it lives in — in RAW plan
// coordinates (the parent floor group carries the iso/flat transform). A soft
// wide under-stroke provides the glow, the core stroke the line. Green =
// secure, red + ripple = open, dimmed gray = unavailable. After a close the
// color walks red -> amber -> green across CONTACT_LINGER_MS at full
// strength.
function ContactSegment({
  def,
  open,
  offline,
  lingerAlpha,
  highlighted,
  onSelect,
}: {
  def: ContactDef;
  open: boolean;
  offline: boolean;
  lingerAlpha: number;
  highlighted?: boolean;
  onSelect?: () => void;
}) {
  const a = { x: def.x1, y: def.y1 };
  const b = { x: def.x2, y: def.y2 };
  let color = offline
    ? 'var(--berg-text-tertiary)'
    : open
      ? 'var(--berg-red)'
      : 'var(--berg-green)';
  if (!open && !offline && lingerAlpha > 0) {
    const t = 1 - lingerAlpha;
    color =
      t < 0.5
        ? `color-mix(in srgb, var(--berg-red) ${Math.round((1 - t * 2) * 100)}%, var(--berg-amber-light))`
        : `color-mix(in srgb, var(--berg-amber-light) ${Math.round((2 - t * 2) * 100)}%, var(--berg-green))`;
  }
  return (
    <g opacity={offline ? 0.4 : 1} style={{ pointerEvents: 'none' }}>
      {/* The drawn marker is inert. A painted SVG shape catches clicks by
          default, so in the perspective view — where no sensor hit target is
          rendered — a tap landing on a dot used to miss the floor rect beneath
          it and fall through to the overlay body, which reads a stray click as
          "close". Only the hit targets below take taps, and they exist only on
          the soloed floor; everywhere else a tap on a sensor now opens that
          sensor's floor, same as tapping the floor around it. */}
      {/* Every contact pings continuously in its current color, all on the
          map's single --ping-grow/--ping-fade clock. */}
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={color}
        strokeLinecap="round"
        style={{
          strokeWidth: 'calc((12 + 208 * var(--ping-grow, 0)) * 1px)',
          opacity: 'calc(0.45 * var(--ping-fade, 0))',
        }}
      />
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={color}
        strokeWidth={30}
        strokeLinecap="round"
        opacity={open ? 0.4 : 0.25}
      />
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={color}
        strokeWidth={11}
        strokeLinecap="round"
      />
      {/* Tapped-event flash (from the overlay timeline). */}
      {highlighted && (
        <line
          x1={a.x}
          y1={a.y}
          x2={b.x}
          y2={b.y}
          stroke="var(--berg-text)"
          strokeWidth={44}
          strokeLinecap="round"
          opacity={0.45}
          className="animate-pulse"
        />
      )}
      {/* Fat invisible hit target for the overlay's tap-for-detail. */}
      {onSelect && (
        <line
          x1={a.x}
          y1={a.y}
          x2={b.x}
          y2={b.y}
          stroke="#000"
          strokeOpacity={0}
          strokeWidth={90}
          strokeLinecap="round"
          style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
          onClick={(ev) => {
            ev.stopPropagation();
            onSelect();
          }}
        />
      )}
    </g>
  );
}

// A camera reads as a triangle that MIRRORS its cone: the wide edge faces the
// field of view and the tip trails behind, so the marker widens the same way
// the wedge does. Front corners at +/-55 degrees off dirDeg, tip at 1.05r
// directly opposite — near enough to equilateral that it stays a triangle
// rather than a dart. The floor group's isometric transform then skews the
// whole thing like everything else on the plan.
function arrowPath(x: number, y: number, dirDeg: number, r: number): string {
  const at = (deg: number, rad: number) => {
    const a = (deg * Math.PI) / 180;
    return `${(x + rad * Math.cos(a)).toFixed(1)},${(y + rad * Math.sin(a)).toFixed(1)}`;
  };
  return `M ${at(dirDeg + 180, r * 1.05)} L ${at(dirDeg + 55, r)} L ${at(dirDeg - 55, r)} Z`;
}

// Dot for a motion sensor, cone-mirroring triangle for a camera. Both the
// marker itself and the ping echo go through here, so the two can never
// disagree about the shape.
function Marker({
  x,
  y,
  r,
  dirDeg,
  ...paint
}: {
  x: number;
  y: number;
  r: number;
  // Omitted for a plain dot; supplied to point the arrowhead.
  dirDeg?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  className?: string;
  style?: CSSProperties;
}) {
  if (dirDeg === undefined) return <circle cx={x} cy={y} r={r} {...paint} />;
  return <path d={arrowPath(x, y, dirDeg, r)} {...paint} />;
}

// A motion sensor: an always-purple pinging dot at the mounting point (raw
// plan coordinates — the parent floor group carries the transform), plus a
// directional coverage cone that only renders while motion is firing.
// Offline = hollow gray.
function MotionDot({
  def,
  state,
  highlighted,
  onSelect,
  tint = 'var(--berg-purple-light)',
  pointed = false,
}: {
  def: MotionDef;
  state: string | undefined;
  highlighted?: boolean;
  onSelect?: () => void;
  // Motion sensors are purple, Ring cameras blue.
  tint?: string;
  // Draw the marker as an arrowhead aimed along dirDeg instead of a dot.
  pointed?: boolean;
}) {
  const offline = isUnavailable(state);
  const active = state === 'on';
  const x = def.cx;
  const y = def.cy;
  const color = offline ? 'var(--berg-text-tertiary)' : tint;
  // Triangles carry less visual weight than a filled disc of the same radius,
  // so cameras get a bigger marker to stay as legible as the sensor dots.
  const markerR = pointed ? 32 : 22;
  // Wedge outline in plan space; the floor group's transform projects it.
  const steps = 14;
  const a0 = ((def.dirDeg - def.spreadDeg / 2) * Math.PI) / 180;
  const a1 = ((def.dirDeg + def.spreadDeg / 2) * Math.PI) / 180;
  const rim: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    rim.push(
      `${(x + def.range * Math.cos(a)).toFixed(1)},${(y + def.range * Math.sin(a)).toFixed(1)}`
    );
  }
  const wedge = `M ${x.toFixed(1)},${y.toFixed(1)} L ${rim.join(' L ')} Z`;
  const gid = `motion-fade-${def.entity.replace(/\./g, '-')}`;
  return (
    // Inert for the same reason as ContactSegment above — see the note there.
    <g style={{ pointerEvents: 'none' }}>
      <defs>
        <radialGradient id={gid} gradientUnits="userSpaceOnUse" cx={x} cy={y} r={def.range}>
          <stop offset="0%" stopColor={color} stopOpacity={0.4} />
          <stop offset="70%" stopColor={color} stopOpacity={0.18} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      {active && !offline && <path d={wedge} fill={`url(#${gid})`} className="animate-pulse" />}
      {/* Ping echo — driven by the map's shared --ping vars. */}
      {!offline && (
        <Marker
          x={x}
          y={y}
          r={markerR}
          dirDeg={pointed ? def.dirDeg : undefined}
          fill={color}
          style={{
            transformBox: 'fill-box',
            transformOrigin: 'center',
            transform: 'scale(calc(1 + 5 * var(--ping-grow, 0)))',
            opacity: 'calc(0.45 * var(--ping-fade, 0))',
          }}
        />
      )}
      <Marker
        x={x}
        y={y}
        r={markerR}
        dirDeg={pointed ? def.dirDeg : undefined}
        fill={offline ? 'none' : color}
        stroke={color}
        strokeWidth={offline ? 8 : 0}
        opacity={active ? 1 : 0.85}
      />
      {/* Tapped-event flash (from the overlay timeline). */}
      {highlighted && (
        <circle
          cx={x}
          cy={y}
          r={80}
          fill="none"
          stroke="var(--berg-text)"
          strokeWidth={10}
          opacity={0.45}
          className="animate-pulse"
        />
      )}
      {/* Fat invisible hit target for the overlay's tap-for-detail. */}
      {onSelect && (
        <circle
          cx={x}
          cy={y}
          r={80}
          fill="#000"
          fillOpacity={0}
          style={{ pointerEvents: 'all', cursor: 'pointer' }}
          onClick={(ev) => {
            ev.stopPropagation();
            onSelect();
          }}
        />
      )}
    </g>
  );
}
